import React, { useEffect, useState } from 'react';
import { graphqlQuery } from '../utils/graphqlApi';
import { getEvalSessionDetail } from '../graphql/queries';

const usd = (v) => (v == null || !Number.isFinite(Number(v)) ? 'N/A' : `$${Number(v).toFixed(4)}`);

const messageText = (m) => {
  if (m == null) return '';
  return typeof m === 'string' ? m : JSON.stringify(m, null, 2);
};

// AWSJSON arrives as a string. Turn a request/response context into a list of
// individual messages:
//   - a JSON array           -> one message per element
//   - any other JSON value   -> a single message (objects are pretty-printed)
//   - plain text             -> split on blank lines. The eval pipeline stores
//                               one string per session with the session's
//                               messages joined by a blank line.
const toMessages = (value) => {
  if (value == null || value === '') return [];
  let parsed = value;
  try {
    parsed = JSON.parse(value);
  } catch {
    // not JSON: treat as plain text
  }
  if (Array.isArray(parsed)) return parsed.map(messageText).filter((t) => t.trim() !== '');
  if (parsed !== null && typeof parsed === 'object') return [messageText(parsed)];
  // Text, or a bare JSON scalar such as `42` / `null` that is really just text.
  return String(typeof parsed === 'string' ? parsed : value)
    .split(/\n\s*\n/)
    .map((t) => t.trim())
    .filter(Boolean);
};

// The evals rows of the session, oldest first. When the backend sends none (for
// example a session that only has a cost row) fall back to the single-row fields
// so the card never loses what the older fields carried.
const recordsOf = (detail) => {
  if (!detail) return [];
  if (Array.isArray(detail.records) && detail.records.length > 0) return detail.records;
  return [
    {
      eval_id: detail.eval_id,
      score: null,
      rationale: detail.rationale,
      request_context: detail.request_context,
      response_context: detail.response_context,
    },
  ];
};

// Flatten one field of every record into messages, each labelled with the eval
// it came from. Records that have nothing in that field are skipped.
const collectMessages = (records, field, withScore = false) =>
  records.flatMap((r) => {
    const score = withScore && r.score != null ? ` · ${Math.round(r.score * 100)}%` : '';
    const label = r.eval_id ? `${r.eval_id}${score}` : null;
    return toMessages(r[field]).map((text) => ({ label, text }));
  });

// Session-scoped cache so re-opening the same session is instant.
const cache = new Map();

export default function EvalSessionDetailModal({ session, evalId, agentId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session) return undefined;
    const key = `${agentId}|${evalId}|${session.session_id}`;
    if (cache.has(key)) {
      setDetail(cache.get(key));
      setError(null);
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setDetail(null);
    graphqlQuery(getEvalSessionDetail, {
      input: { session_id: session.session_id, eval_id: evalId, agent_id: agentId },
    })
      .then((data) => {
        if (cancelled) return;
        const d = data?.getEvalSessionDetail ?? null;
        cache.set(key, d);
        setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session, evalId, agentId]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!session) return null;

  // Every evals row of the session feeds the three message columns.
  const records = recordsOf(detail);
  const rationales = collectMessages(records, 'rationale', true);
  const requests = collectMessages(records, 'request_context');
  const responses = collectMessages(records, 'response_context');

  // Placeholder for the columns that come from the detail query.
  const pending = isLoading ? '…' : '—';

  // All messages of one column (rationale, request or response), stacked one
  // below the other.
  const renderMessages = (list) => {
    if (!detail) return pending;
    if (list.length === 0) return '—';
    return list.map((m, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={i} className="ge-msg">
        <span className="ge-msg-n">{i + 1}.</span>
        <div className="ge-msg-body">
          {m.label && <div className="ge-msg-label">{m.label}</div>}
          <span className="ge-cell-text">{m.text}</span>
        </div>
      </div>
    ));
  };

  return (
    <div className="ge-modal-backdrop" onClick={onClose}>
      <div
        className="ge-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ge-session-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ge-modal-header">
          <h3 id="ge-session-title">Session details</h3>
          <button type="button" className="ge-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {isLoading && <div className="ge-muted" style={{ marginTop: 12 }}>Loading session…</div>}
        {error && <div className="ge-bad" style={{ marginTop: 12 }}>{error}</div>}
        {!isLoading && !error && detail === null && (
          <div className="ge-muted" style={{ marginTop: 12 }}>No stored detail for this session.</div>
        )}

        <div className="ge-modal-scroll">
          <table className="ge-modal-table ge-session-table">
            <thead>
              <tr>
                <th>Session id</th>
                <th>Score</th>
                <th>Feedback</th>
                <th>Routed to</th>
                <th>Cost</th>
                <th>Rationale</th>
                <th>Request context</th>
                <th>Response context</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {/* Known from the report row, so shown immediately */}
                <td className="ge-skill">{session.session_id}</td>
                <td>{session.score != null ? `${Math.round(session.score * 100)}%` : 'N/A'}</td>
                <td>{session.sentiment || '—'}</td>
                {/* From getEvalSessionDetail */}
                <td className="ge-skill">{detail ? detail.routed_to || '—' : pending}</td>
                <td>
                  {detail
                    ? `${usd(detail.total_cost)}${
                        detail.currency && detail.currency !== 'USD' ? ` ${detail.currency}` : ''
                      }`
                    : pending}
                </td>
                <td>{renderMessages(rationales)}</td>
                <td>{renderMessages(requests)}</td>
                <td>{renderMessages(responses)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
